import { evaluateSectionVisibility, evaluateFieldVisibility } from "../schema/evaluateCondition";
import FormField from "./FormField";
import Button from "../../ui/Button";
import "./_dynamic-form.scss";

// Field types whose content doesn't fit a half-width column (long free
// text, rich content) - used to mark each field's wrapper with `--full` so
// a 2-column-grid consumer (currently only MarketFormModal's scoped
// override - see its own scss) knows which fields must take the whole row.
// A no-op for every other consumer: `__section-inputs` stays a plain
// single-column flex column by default, where these classes carry no
// styling at all.
//
// multiSelect is deliberately NOT here - Market's own `country` field is a
// multiSelect but reads fine at half width (a couple of chips), and Market
// wants every field except its textArea ("Notes") paired two-per-row.
const FULL_WIDTH_FIELD_TYPES = new Set([
  "textArea",
  "richEditor",
  "textEditor",
  "imageUpload",
  "arrayText",
  "listManager",
  "tieredPricing",
  "walletCreditsDynamic",
  "permissionSelector",
  "permissionSelectorCustom",
  "checkboxContainer",
]);
// `field.fullWidth` is an opt-in per-field override (e.g. Usage Source's
// `originSource` - see usageSourceFormRules.js's packFieldsForTwoColumnGrid)
// for a field whose type isn't inherently full-width but a consumer wants
// visually anchored as its own row anyway.
const isFullWidthField = (field) => FULL_WIDTH_FIELD_TYPES.has(field.type) || field.fullWidth === true;

/**
 * Renders a dynamic (object-customization) form schema: each section
 * conditionally shown, each field routed through FormField (native
 * _components/ui input or the legacy renderInput bridge). Pairs with
 * useDynamicForm, which supplies `control`/`formData`/`errors`/
 * `handleChange`/`handleBlur` from a single react-hook-form instance.
 *
 * Re-evaluates section visibility on every render because `formData` comes
 * from react-hook-form's `watch()` (see useDynamicForm), which already
 * re-renders on every field change - no forced-render trigger needed here.
 *
 * `disabled`: force every field inert regardless of its own showCondition/
 * disabledCondition - the caller's "a create/update mutation is in flight"
 * signal, so a modal doesn't need to thread that onto each field descriptor
 * itself.
 */
const DynamicForm = ({
  schema,
  control,
  formData,
  errors,
  handleChange,
  handleBlur,
  mode = "create",
  showToast,
  setImageFile,
  setShouldDeleteImage,
  context,
  filters,
  disabled = false,
}) => {
  if (!Array.isArray(schema)) return null;

  return (
    <>
      {schema.map((section, idx) => {
        if (!evaluateSectionVisibility(section, formData, schema)) return null;

        // An empty groupName (Market's own schema - see marketFormRules.js's
        // stripSectionHeading) means "no heading for this section", not "a
        // heading with no text" - the design's own Add-drawer has no
        // section heading at all, just the field grid straight under the
        // title/blurb, and rendering an empty `<h3>` still ate the header
        // block's own margin-bottom for nothing.
        const hasHeader = Boolean(section.section) || Boolean(section.btn);

        return (
          <div key={idx} className="dynamic-form__section">
            {hasHeader && (
              <div className="dynamic-form__section-header">
                <h3 className="dynamic-form__section-title">{section.section}</h3>
                {section.btn && (
                  <Button
                    type="button"
                    className="dynamic-form__section-button"
                    onClick={section.btn.onClick}
                    variant="text"
                    size="xs"
                  >
                    {section.btn.label}
                  </Button>
                )}
              </div>
            )}

            <div className="dynamic-form__section-inputs">
              {(section.fields || [])
                // A hidden (showCondition-false) or "button"-type field
                // renders nothing from FormField - skip its wrapper too, not
                // just its content. A flex column never showed the
                // difference (an empty div is zero-height either way), but
                // a 2-column grid consumer (e.g. CompanyFormModal's own
                // scoped override) still counts an empty wrapper as an
                // occupied cell, silently shifting every later field one
                // slot out of place.
                .filter(
                  (field) =>
                    field.type !== "button" && evaluateFieldVisibility(field, formData, schema)
                )
                .map((field) => (
                  <div
                    key={field.name}
                    className={`dynamic-form__field-wrap ${
                      isFullWidthField(field) ? "dynamic-form__field-wrap--full" : ""
                    }`}
                  >
                    <FormField
                      field={field}
                      control={control}
                      formData={formData}
                      errors={errors}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      allFields={schema}
                      mode={mode}
                      showToast={showToast}
                      setImageFile={setImageFile}
                      setShouldDeleteImage={setShouldDeleteImage}
                      context={context}
                      filters={filters}
                      disabled={disabled}
                    />
                  </div>
                ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default DynamicForm;
